import { Button, DataTable, EmptyState, Pagination, Panel, Pill, SearchInput, SectionHeader, useClientTable, type DataTableSort } from '@bcecloud/portal-ui';
import { receivedLabel, type LeadPanel, type PanelLead } from '@/lib/lead-panel';
import { EventCell, LeadCell, SalesforceLink } from './LeadCell';

const RECENT_SORT_KEYS = ['createdAt', 'company', 'ownerName', 'isConverted'] as const;
const DEFAULT_SORT: DataTableSort = { key: 'createdAt', direction: 'desc' };

export interface RecentLeadsTableProps {
  /** The section heading and its line underneath; the search sits beside them. */
  readonly title: string;
  readonly description: string;
  readonly panel: LeadPanel;
  readonly onConvert: (lead: PanelLead) => void;
}

/** The reps' leads from today and yesterday, converted or not, with Convert on the open ones. */
export function RecentLeadsTable({ title, description, panel, onConvert }: RecentLeadsTableProps): React.ReactElement {
  const table = useClientTable<PanelLead>({
    rows: panel.recent,
    searchText: (lead) => [lead.company, lead.contactName, lead.email, lead.ownerName, lead.eventCity, lead.program].join(' '),
    sortKeys: RECENT_SORT_KEYS,
    defaultSort: DEFAULT_SORT,
    prefix: 'recent',
  });

  if (panel.recent.length === 0) {
    return (
      <>
        <SectionHeader title={title} description={description} />
        <Panel>
          <EmptyState title="No leads yet today" message="Leads given to a rep today or yesterday show here, with Convert on the ones still open." />
        </Panel>
      </>
    );
  }

  return (
    <>
      <SectionHeader
        title={title}
        description={description}
        aside={
          <SearchInput
            label="Search today's and yesterday's leads"
            placeholder="Company, contact, rep or city"
            value={table.search}
            onChange={(event) => table.setSearch(event.target.value)}
            className="w-96 max-w-full"
          />
        }
      />
      <DataTable
        headers={[
          { label: 'Received', sortKey: 'createdAt' },
          { label: 'Lead', sortKey: 'company' },
          { label: 'Rep', sortKey: 'ownerName' },
          'Event',
          { label: 'Status', sortKey: 'isConverted' },
          'Actions',
        ]}
        visuallyHiddenHeaders={[5]}
        sort={table.sort}
        onSort={table.onSort}
        minWidth={940}
      >
        {table.pageRows.map((lead) => (
          <tr key={lead.id} className="border-t border-portal-line align-middle">
            <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{receivedLabel(lead.createdAt, panel)}</td>
            <td className="min-w-64 p-3 text-portal-small">
              <LeadCell lead={lead} />
            </td>
            <td className="p-3 text-portal-small whitespace-nowrap">{lead.ownerName ?? 'Unknown'}</td>
            <td className="p-3 text-portal-small">
              <EventCell lead={lead} />
            </td>
            <td className="p-3 text-portal-small whitespace-nowrap">
              {lead.isConverted ? <Pill tone="success">Converted</Pill> : <Pill tone="info">{lead.status ?? 'Open'}</Pill>}
            </td>
            <td className="p-3">
              <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                {lead.isConverted ? null : (
                  <Button size="compact" variant="secondary" onClick={() => onConvert(lead)}>
                    Convert
                  </Button>
                )}
                <SalesforceLink lead={lead} />
              </div>
            </td>
          </tr>
        ))}
      </DataTable>
      <Pagination page={table.page} totalPages={table.totalPages} onChange={table.setPage} summary={table.summary} className="mt-3" />
    </>
  );
}
